import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { PATHS } from '../../routes/paths';
import { useAppSelector } from '../../store/hooks';
import { selectSession } from '../../Module/Auth/Presentation/authSelectors';
import { selectUserProfile } from '../../Module/User/Presentation/userSelectors';
import { formatUserFullName } from '../../Utils/formatUserName';
import { getOrganizationDisplayName } from '../../Utils/organizationOptions';
import {
  isAuthenticationImageMarkedUploadedOnProfile,
  isRegistrationCardMarkedUploadedOnProfile,
} from '../MyAccount/useMyAccount';

/** 기부계좌 신청 회원 유형 — 개인 / 단체 */
export type GivingAccountMemberType = 'individual' | 'organization';

export const GIVING_ACCOUNT_MEMBER_TYPES: GivingAccountMemberType[] = ['individual', 'organization'];

/**
 * 신청 단계
 * 1. 회원 유형 선택 (개인 기부계좌 / 단체 기부계좌)
 * 2. 기부계좌 설명 + 확인 및 동의 (유형별로 다름)
 * 3. 정보 입력 + 증빙서류 확인 (단체만 단체명·단체 소개글 입력)
 */
export type GivingAccountApplyStep = 1 | 2 | 3;

/** 동의 화면 내용 — 번역 파일 page.givingAccountApply.agreement.{individual|organization} (docs/기부계좌신청동의.md 기준) */
export type GivingAccountAgreementSection = {
  title: string;
  body?: string;
  items?: string[];
  note?: string;
};

export type GivingAccountAgreementContent = {
  title: string;
  intro: string;
  sections: GivingAccountAgreementSection[];
  checksTitle: string;
  checks: string[];
};

/** 증빙서류 — 등록은 나의 계정 화면의 해당 탭에서 한다 */
export type GivingAccountDocumentKey = 'certificate' | 'idCard';

const DOCUMENT_REGISTER_TAB: Record<GivingAccountDocumentKey, string> = {
  certificate: 'ministry', // 소속증명서 → 사역정보
  idCard: 'verification', // 신분증 사본 → 본인확인서류
};

/** 신청 시 전송할 값 (신청 API 가 나오면 이 값을 보낸다). 단체명·소개글은 단체일 때만 */
export type GivingAccountApplication = {
  user_id: string;
  member_type: GivingAccountMemberType;
  organization?: string;
  organization_desc?: string;
};

export const useGivingAccountApply = () => {
  const navigate = useNavigate();
  const { t } = useTranslation('translation');
  const session = useAppSelector(selectSession);
  const userProfile = useAppSelector(selectUserProfile);

  const [step, setStep] = useState<GivingAccountApplyStep>(1);
  const [memberType, setMemberType] = useState<GivingAccountMemberType | ''>('');
  const [agreementChecks, setAgreementChecks] = useState<boolean[]>([]);
  const [organization, setOrganization] = useState('');
  const [organizationDesc, setOrganizationDesc] = useState('');

  const isOrganization = memberType === 'organization';

  // 2단계 내용 — 선택한 유형의 안내·동의 문안
  const agreement = useMemo<GivingAccountAgreementContent | null>(() => {
    if (memberType === '') return null;
    const content = t(`page.givingAccountApply.agreement.${memberType}`, {
      returnObjects: true,
      defaultValue: null,
    }) as unknown as GivingAccountAgreementContent | null;
    return content && Array.isArray(content.sections) && Array.isArray(content.checks) ? content : null;
  }, [memberType, t]);

  // 1단계에서 유형을 바꾸면 동의 체크는 처음부터 다시 받는다
  const handleSelectMemberType = useCallback((type: GivingAccountMemberType) => {
    setMemberType((current) => {
      if (current !== type) setAgreementChecks([]);
      return type;
    });
  }, []);

  const toggleAgreementCheck = useCallback((index: number) => {
    setAgreementChecks((current) => {
      const next = [...current];
      next[index] = !next[index];
      return next;
    });
  }, []);

  const allAgreementChecked =
    agreement !== null && agreement.checks.length > 0 && agreement.checks.every((_, index) => agreementChecks[index]);

  // 가입할 때 입력한 단체명을 기본값으로 보여 준다 (프로필이 늦게 도착해도 한 번만 채운다)
  const organizationPrefilledRef = useRef(false);
  useEffect(() => {
    if (organizationPrefilledRef.current || !userProfile) return;
    organizationPrefilledRef.current = true;
    const saved = typeof userProfile.organization === 'string' ? userProfile.organization : '';
    setOrganization((current) => current || getOrganizationDisplayName(saved));
  }, [userProfile]);

  const applicantName = useMemo(() => {
    const user = session?.user;
    return (
      formatUserFullName(userProfile?.first_name, userProfile?.last_name) ||
      formatUserFullName(user?.first_name, user?.last_name)
    );
  }, [session, userProfile]);

  const applicantEmail = String(userProfile?.email || session?.user?.email || '');

  const documents: Array<{ key: GivingAccountDocumentKey; registered: boolean }> = [
    { key: 'certificate', registered: isAuthenticationImageMarkedUploadedOnProfile(userProfile, null) },
    { key: 'idCard', registered: isRegistrationCardMarkedUploadedOnProfile(userProfile, null) },
  ];
  const allDocumentsRegistered = documents.every((doc) => doc.registered);

  // 단체는 단체명·소개글까지 있어야 하고, 개인은 서류만 있으면 된다
  const organizationFilled = organization.trim().length > 0 && organizationDesc.trim().length > 0;
  const canSubmit = memberType !== '' && allDocumentsRegistered && (!isOrganization || organizationFilled);

  // 1·2단계 "다음" 활성 조건
  const canGoNext = step === 1 ? memberType !== '' : step === 2 ? allAgreementChecked : false;

  const handleNext = useCallback(() => {
    if (!canGoNext) return;
    setStep((current) => (current === 1 ? 2 : current === 2 ? 3 : current));
  }, [canGoNext]);

  const handleRegisterDocument = useCallback(
    (key: GivingAccountDocumentKey) => {
      navigate(`${PATHS.MY_ACCOUNT}?tab=${DOCUMENT_REGISTER_TAB[key]}`);
    },
    [navigate],
  );

  const handleCancel = useCallback(() => {
    navigate(PATHS.PROJECT_MANAGEMENT);
  }, [navigate]);

  const handleSubmit = useCallback(() => {
    if (!canSubmit) return;

    const application: GivingAccountApplication = {
      user_id: String(session?.user?.user_id ?? session?.user?.id ?? ''),
      member_type: memberType,
      ...(memberType === 'organization'
        ? { organization: organization.trim(), organization_desc: organizationDesc.trim() }
        : {}),
    };

    // 기부계좌 신청 API 가 아직 없다 — API 가 나오면 여기서 application 을 전송한다
    console.info('Giving account application (not sent):', application.member_type);
    window.alert(t('page.givingAccountApply.messages.notReady'));
  }, [canSubmit, memberType, organization, organizationDesc, session, t]);

  return {
    step,
    memberType,
    isOrganization,
    handleSelectMemberType,
    agreement,
    agreementChecks,
    toggleAgreementCheck,
    canGoNext,
    handleNext,
    applicantName,
    applicantEmail,
    documents,
    organization,
    setOrganization,
    organizationDesc,
    setOrganizationDesc,
    canSubmit,
    handleRegisterDocument,
    handleCancel,
    handleSubmit,
  };
};
